import { useState, useRef } from 'react';

const STORAGE_KEY = 'tradrin-chart-templates';

function loadTemplates() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch { return []; }
}

function saveTemplates(templates) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

/**
 * Template Manager — save, load, delete, export, import chart setups.
 *
 * A template captures:
 * - chartType, showVolume
 * - selectedTfs, tfChartTypes
 * - primaryIndicators (activeIndicators config)
 * - tfIndicators (per-TF indicator configs)
 */
export default function TemplateManager({
  // Current state to capture
  chartType,
  showVolume,
  selectedTfs,
  activeIndicators,
  drawings,
  // Callbacks to restore state
  onLoad,
}) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(loadTemplates);
  const [saveName, setSaveName] = useState('');
  const fileInputRef = useRef(null);

  const captureTemplate = (name) => ({
      name,
      createdAt: new Date().toISOString(),
      chartType,
      showVolume,
      selectedTfs,
      activeIndicators: activeIndicators.map(({ indicatorId, instanceId, params }) => ({
        indicatorId, instanceId, params,
      })),
      drawings: drawings || [],
  });

  const handleSave = () => {
    const name = saveName.trim();
    if (!name) return;
    const template = captureTemplate(name);
    const updated = [...templates.filter((t) => t.name !== name), template];
    setTemplates(updated);
    saveTemplates(updated);
    setSaveName('');
  };

  const handleLoad = (template) => {
    if (onLoad) onLoad(template);
    setOpen(false);
  };

  const handleDelete = (name) => {
    const updated = templates.filter((t) => t.name !== name);
    setTemplates(updated);
    saveTemplates(updated);
  };

  const handleExport = (template) => {
    const json = JSON.stringify(template, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    const json = JSON.stringify(templates, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chart-templates.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        // Support single template or array
        const imported = Array.isArray(data) ? data : [data];
        const valid = imported.filter((t) => t.name && t.activeIndicators);
        if (valid.length === 0) {
          alert('Invalid template file.');
          return;
        }
        const merged = [...templates];
        for (const t of valid) {
          const idx = merged.findIndex((m) => m.name === t.name);
          if (idx >= 0) merged[idx] = t;
          else merged.push(t);
        }
        setTemplates(merged);
        saveTemplates(merged);
      } catch {
        alert('Failed to parse template file.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleCopyLink = (template) => {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(template))));
    const text = `template:${encoded}`;
    navigator.clipboard.writeText(text).then(() => {
      alert('Template copied to clipboard! Share this text with others.');
    }).catch(() => {
      // Fallback
      prompt('Copy this template code:', text);
    });
  };

  const handlePasteImport = () => {
    const text = prompt('Paste a shared template code:');
    if (!text) return;
    try {
      let data;
      if (text.startsWith('template:')) {
        data = JSON.parse(decodeURIComponent(escape(atob(text.slice(9)))));
      } else {
        data = JSON.parse(text);
      }
      if (!data.name || !data.activeIndicators) {
        alert('Invalid template.');
        return;
      }
      const updated = [...templates.filter((t) => t.name !== data.name), data];
      setTemplates(updated);
      saveTemplates(updated);
    } catch {
      alert('Invalid template format.');
    }
  };

  if (!open) {
    return (
      <button className="template-toggle-btn" onClick={() => setOpen(true)} title="Templates">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
        </svg>
      </button>
    );
  }

  return (
    <div className="template-manager">
      <div className="template-header">
        <strong>Templates</strong>
        <button className="template-close" onClick={() => setOpen(false)}>&times;</button>
      </div>

      {/* Save */}
      <div className="template-save-row">
        <input
          type="text"
          placeholder="Template name..."
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          className="template-input"
        />
        <button className="template-btn template-btn--save" onClick={handleSave} disabled={!saveName.trim()}>
          Save
        </button>
      </div>

      {/* Template list */}
      <div className="template-list">
        {templates.length === 0 && (
          <div className="template-empty">No saved templates</div>
        )}
        {templates.map((t) => (
          <div key={t.name} className="template-item">
            <div className="template-item-info">
              <span className="template-item-name">{t.name}</span>
              <span className="template-item-meta">
                {t.activeIndicators?.length || 0} indicators
                {t.drawings?.length > 0 && ` / ${t.drawings.length} drawings`}
                {t.selectedTfs?.length > 0 && ` / ${t.selectedTfs.length} TFs`}
              </span>
            </div>
            <div className="template-item-actions">
              <button onClick={() => handleLoad(t)} title="Load">Load</button>
              <button onClick={() => handleExport(t)} title="Export JSON">Export</button>
              <button onClick={() => handleCopyLink(t)} title="Copy shareable code">Share</button>
              <button onClick={() => handleDelete(t.name)} title="Delete" className="template-btn--del">&times;</button>
            </div>
          </div>
        ))}
      </div>

      {/* Import */}
      <div className="template-import-row">
        <button className="template-btn" onClick={() => fileInputRef.current?.click()}>Import JSON</button>
        <button className="template-btn" onClick={handlePasteImport}>Paste Code</button>
        {templates.length > 0 && (
          <button className="template-btn" onClick={handleExportAll}>Export All</button>
        )}
        <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
